import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { ROOT_API } from "../../../api/config";
import { formatDateTime } from "../../../utils/dates";
import "../subscriptions/style.scss";
import "./style.scss";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
});

const euro = (minor, currency) =>
  minor != null ? `${(minor / 100).toFixed(2)} ${currency || "EUR"}` : "—";

const PLAN_LABEL = {
  free: "Free",
  premium: "Premium",
  premium_yearly: "Premium",
  business: "Business",
  credits: "SMS",
};

export default function Bills() {
  const intl = useIntl();
  const selectedLocale =
    useSelector((state) => state.lang.value) ||
    localStorage.getItem("lang") ||
    "hr";
  const locale = selectedLocale === "en" ? "en-US" : "hr-HR";

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${ROOT_API}apis/admin/payments`, {
        headers: authHeaders(),
      });
      setPayments(res?.data?.data || []);
    } catch (e) {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markInvoice = async (paymentId) => {
    try {
      setBusy(`inv${paymentId}`);
      await axios.post(
        `${ROOT_API}apis/admin/mark-invoice`,
        { payment_id: paymentId },
        { headers: authHeaders() }
      );
      await load();
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: intl.formatMessage({ id: "there_are_error_occured" }),
      });
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-subs__state">
        {intl.formatMessage({ id: "dashboard_loading" })}
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const rows = q
    ? payments.filter(
        (p) =>
          (p.email || "").toLowerCase().includes(q) ||
          (p.full_name || "").toLowerCase().includes(q) ||
          (p.invoice_number || "").toLowerCase().includes(q) ||
          (p.revolut_order_id || "").toLowerCase().includes(q)
      )
    : payments;

  const totalCollected = payments
    .filter((p) => p.state === "completed")
    .reduce((sum, p) => sum + (p.amount_minor || 0), 0);

  return (
    <div className="admin-subs">
      <div className="admin-bills__summary">
        {intl.formatMessage({ id: "admin_bills_total" })}:{" "}
        <strong>{euro(totalCollected, "EUR")}</strong>
      </div>
      <input
        className="admin-subs__search"
        placeholder={intl.formatMessage({ id: "admin_bills_search" })}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <table className="admin-subs__table">
        <thead>
          <tr>
            <th>{intl.formatMessage({ id: "admin_bills_invoice" })}</th>
            <th>{intl.formatMessage({ id: "admin_col_user" })}</th>
            <th>{intl.formatMessage({ id: "admin_bills_plan" })}</th>
            <th>{intl.formatMessage({ id: "admin_bills_amount" })}</th>
            <th>{intl.formatMessage({ id: "admin_bills_when" })}</th>
            <th>{intl.formatMessage({ id: "admin_bills_state" })}</th>
            <th>{intl.formatMessage({ id: "admin_bills_revolut" })}</th>
            <th>{intl.formatMessage({ id: "admin_col_actions" })}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id}>
              <td>
                <strong>{p.invoice_number || "—"}</strong>
              </td>
              <td>
                <div className="admin-subs__name">{p.full_name || "—"}</div>
                <div className="admin-subs__email">{p.email}</div>
              </td>
              <td>{PLAN_LABEL[p.plan] || p.plan}</td>
              <td>{euro(p.amount_minor, p.currency)}</td>
              <td>{formatDateTime(p.completed_at || p.created_at, locale)}</td>
              <td>
                <span className={`admin-bills__state admin-bills__state--${p.state}`}>
                  {intl.formatMessage({ id: `admin_bills_state_${p.state}` })}
                </span>
              </td>
              <td className="admin-subs__email">{p.revolut_order_id}</td>
              <td>
                {p.state === "completed" ? (
                  <div>
                    <span
                      className={`admin-subs__inv admin-subs__inv--${p.invoice_status}`}
                    >
                      {intl.formatMessage({
                        id:
                          p.invoice_status === "issued"
                            ? "admin_invoice_issued"
                            : "admin_invoice_pending",
                      })}
                    </span>
                    {p.invoice_status === "pending" ? (
                      <button
                        type="button"
                        className="admin-subs__btn admin-subs__btn--small"
                        disabled={busy === `inv${p.id}`}
                        onClick={() => markInvoice(p.id)}
                      >
                        {intl.formatMessage({ id: "admin_mark_invoice" })}
                      </button>
                    ) : null}
                  </div>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
